/**
 * Print & Export Types and Interfaces
 *
 * Defines all print/export-related TypeScript interfaces for the GeoVera WebGIS Platform.
 *
 * Supports:
 * - PDF printing with configurable page sizes, orientation, and DPI
 * - Image export (PNG, JPG, SVG) with custom dimensions
 * - Layout composition with draggable elements
 * - Layout template persistence
 *
 * **Validates: Requirements 36, 37, 38**
 */

// ---------------------------------------------------------------------------
// Page sizes
// ---------------------------------------------------------------------------

/** Supported page size identifiers */
export type PageSize = 'A4' | 'A3' | 'Letter' | 'Custom';

/** Page orientation */
export type Orientation = 'portrait' | 'landscape';

/** DPI options for print quality */
export type DPI = 72 | 150 | 300;

/** Image export format */
export type ImageFormat = 'png' | 'jpg' | 'svg';

// ---------------------------------------------------------------------------
// Page dimensions in mm
// ---------------------------------------------------------------------------

export interface PageDimensions {
  width: number;
  height: number;
}

/** Standard page dimensions in millimeters */
export const PAGE_DIMENSIONS: Record<Exclude<PageSize, 'Custom'>, PageDimensions> = {
  A4: { width: 210, height: 297 },
  A3: { width: 297, height: 420 },
  Letter: { width: 216, height: 279 },
};

// ---------------------------------------------------------------------------
// Print options
// ---------------------------------------------------------------------------

export interface PrintOptions {
  pageSize: PageSize;
  orientation: Orientation;
  customWidth?: number;
  customHeight?: number;
  includeTitle: boolean;
  title?: string;
  includeLegend: boolean;
  includeScaleBar: boolean;
  includeNorthArrow: boolean;
  dpi: DPI;
}

export const DEFAULT_PRINT_OPTIONS: PrintOptions = {
  pageSize: 'A4',
  orientation: 'portrait',
  includeTitle: true,
  title: 'Map',
  includeLegend: true,
  includeScaleBar: true,
  includeNorthArrow: true,
  dpi: 150,
};

// ---------------------------------------------------------------------------
// Image export options
// ---------------------------------------------------------------------------

export interface ImageExportOptions {
  format: ImageFormat;
  width: number;
  height: number;
  dpi: DPI;
  preserveTransparency: boolean;
}

export const DEFAULT_IMAGE_EXPORT_OPTIONS: ImageExportOptions = {
  format: 'png',
  width: 1920,
  height: 1080,
  dpi: 150,
  preserveTransparency: true,
};

// ---------------------------------------------------------------------------
// Layout elements
// ---------------------------------------------------------------------------

/** Types of elements that can be placed in a layout */
export type LayoutElementType = 'map' | 'title' | 'legend' | 'scale-bar' | 'north-arrow' | 'text';

/** A single element in the layout composer */
export interface LayoutElement {
  id: string;
  type: LayoutElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  style?: Record<string, string | number>;
  content?: string;
}

/** Complete map layout with all elements */
export interface MapLayout {
  mapFrame: LayoutElement;
  title?: LayoutElement;
  legend?: LayoutElement;
  scaleBar?: LayoutElement;
  northArrow?: LayoutElement;
  textBoxes: LayoutElement[];
}

// ---------------------------------------------------------------------------
// Layout templates
// ---------------------------------------------------------------------------

/** A saved layout template */
export interface LayoutTemplate {
  id: string;
  userId?: string;
  name: string;
  layout: MapLayout;
  createdAt?: string;
  updatedAt?: string;
}

// ---------------------------------------------------------------------------
// Print/Export state for Redux
// ---------------------------------------------------------------------------

export interface PrintExportState {
  printDialogOpen: boolean;
  imageExportDialogOpen: boolean;
  layoutComposerOpen: boolean;
  printOptions: PrintOptions;
  imageExportOptions: ImageExportOptions;
  currentLayout: MapLayout | null;
  layoutTemplates: LayoutTemplate[];
  isExporting: boolean;
  exportError: string | null;
}
